import { distance } from "@turf/turf";
import type { Feature, Point } from "geojson";
import Supercluster from "supercluster";

import type { Store } from "@/services/features/StoreService";

export const STACK_RADIUS_METERS = 100;
export const CLUSTER_MAX_ZOOM = 20;
export const SELECTED_STORE_ZOOM = 16;

export type StoreStack = {
  id: number;
  longitude: number;
  latitude: number;
  stores: Store[];
};

type StackProperties = { stackId: number; storeCount: number };
type ClusterProperties = { storeCount: number };

/** Stable, bounded groups: every pair must be within the radius, avoiding chains across a city. */
export const buildStoreStacks = (
  stores: Store[],
  radiusMeters = STACK_RADIUS_METERS,
): StoreStack[] => {
  const stacks: StoreStack[] = [];
  for (const store of [...stores].sort((a, b) => a.id - b.id)) {
    const { lat, lon } = store.geoPoint ?? {};
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180)
      continue;
    const stack = stacks.find((candidate) =>
      candidate.stores.every(
        (member) =>
          distance([lon, lat], [member.geoPoint.lon, member.geoPoint.lat], { units: "meters" }) <=
          radiusMeters,
      ),
    );
    if (stack) {
      stack.stores.push(store);
      // Average longitude on the unit circle so stacks can straddle the antimeridian.
      const radians = stack.stores.map((member) => (member.geoPoint.lon * Math.PI) / 180);
      stack.longitude =
        (Math.atan2(
          radians.reduce((sum, value) => sum + Math.sin(value), 0),
          radians.reduce((sum, value) => sum + Math.cos(value), 0),
        ) *
          180) /
        Math.PI;
      stack.latitude =
        stack.stores.reduce((sum, member) => sum + member.geoPoint.lat, 0) / stack.stores.length;
    } else {
      stacks.push({ id: store.id, longitude: lon, latitude: lat, stores: [store] });
    }
  }
  return stacks;
};

export const stackFeature = (stack: StoreStack): Feature<Point, StackProperties> => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: [stack.longitude, stack.latitude] },
  properties: { stackId: stack.id, storeCount: stack.stores.length },
});

export const createStoreClusterIndex = (stacks: StoreStack[]) =>
  new Supercluster<StackProperties, ClusterProperties>({
    radius: 56,
    maxZoom: CLUSTER_MAX_ZOOM,
    map: (properties) => ({ storeCount: properties.storeCount }),
    reduce: (accumulated, properties) => {
      accumulated.storeCount += properties.storeCount;
    },
  }).load(stacks.map(stackFeature));

/** Physical map offsets: two petals are vertical, three triangular, four a diamond. */
export const getClusterPetalOffsets = (count: number, markerSize: number): [number, number][] =>
  Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    return [Math.cos(angle) * markerSize * 0.7, Math.sin(angle) * markerSize * 0.7];
  });

/** Screen-space rings keep every revealed marker separate, even for large stacks. */
export const getStackOffsets = (count: number, markerSize: number): [number, number][] => {
  const offsets: [number, number][] = [];
  const spacing = markerSize + 8;
  for (let ring = 1; offsets.length < count; ring += 1) {
    const radius = ring * (markerSize + 10);
    const capacity = Math.max(2, Math.floor((2 * Math.PI * radius) / spacing));
    const remaining = Math.min(capacity, count - offsets.length);
    for (let i = 0; i < remaining; i += 1) {
      const angle = (i / remaining) * 2 * Math.PI - Math.PI / 2;
      offsets.push([Math.cos(angle) * radius, Math.sin(angle) * radius]);
    }
  }
  return offsets;
};

/** Matches groups by their stores, rather than cluster IDs which change with zoom. */
export const matchClusterMarkers = <T extends { key: string; storeIds: readonly number[] }>(
  sources: readonly T[],
  targets: readonly T[],
): Map<string, T> => {
  const sourceByStore = new Map(
    sources.flatMap((source) => source.storeIds.map((id) => [id, source] as const)),
  );
  const matches = new Map<string, T>();
  for (const target of targets) {
    const votes = new Map<T, number>();
    let best: T | undefined;
    let bestCount = 0;
    for (const id of target.storeIds) {
      const source = sourceByStore.get(id);
      if (!source) continue;
      const count = (votes.get(source) ?? 0) + 1;
      votes.set(source, count);
      if (count > bestCount) {
        best = source;
        bestCount = count;
      }
    }
    if (best) matches.set(target.key, best);
  }
  return matches;
};
